import parse, { domToReact } from "html-react-parser";
import moment from "moment";
import { useState } from "react";
import { useMutation } from "@apollo/client";
import { Box, Button, Divider, Grid, Tooltip, Typography } from "@mui/material";
import { UPDATE_COMMENT_LIKE } from "../../../graphql/mutations/NewsContext";
import Avatar from "../../common/Avatar";
import Mention from "../../common/Mentions/Mention";
import EmoticonDisplay from "./EmoticonDisplay";
import ReactionButton from "./ReactionButton";

// Function to calculate time difference in a user-friendly format
const getTimeAgo = (date) => {
  const now = moment();
  const duration = moment.duration(now.diff(moment(date)));
  if (duration.asMinutes() < 60) return "< 1 h";
  if (duration.asHours() < 24) return `${Math.floor(duration.asHours())} h`;
  if (duration.asDays() < 30) return `${Math.floor(duration.asDays())} d`;
  if (duration.asMonths() < 36) return `${Math.floor(duration.asMonths())} m`;
  return `${Math.floor(duration.asYears())} y`;
};

const Comment = ({ comment, onLikeComment }) => {

  const renderLeadTextWithMentions = (leadText, mentions) => {
    const mentionRegex = /<@m(\d+)>/g;

    const replaceMentions = (node) => {
      if (node.type === "text") {
        const text = node.data;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
          const mentionId = parseInt(match[1], 10);
          const mentionData = mentions.find((m) => m.mentionId === mentionId);

          // Add preceding text
          if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
          }

          // Add Mention component or fallback text
          if (mentionData) {
            parts.push(
              <Mention

                key={mentionId}
                mentionId={mentionId}
                mentions={mentionData}
              />
            );
          } else {
            parts.push(match[0]); // Raw text if no matching mention
          }

          lastIndex = mentionRegex.lastIndex;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          parts.push(text.substring(lastIndex));
        }

        // Wrap in a single parent element
        return <>{parts}</>;
      }

      // Recursively process child nodes for nested HTML
      if (node.children) {
        return {
          ...node,
          children: domToReact(node.children, { replace: replaceMentions }),
        };
      }

      return node;
    };

    return parse(leadText, { replace: replaceMentions });
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        borderRadius: 1,
        p: 1,
        display: "flex",
        flexDirection: "column",
        mb: 0.5,
      }}
    >
      {/* Author and Avatar */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Avatar
          author={comment.username}
          url={comment.avatarUrl}
          sx={{ width: 32, height: 32, mr: 1 }}
        />
        <Typography
          sx={{ fontSize: "15px", fontWeight: 500, lineHeight: 1 }}
        >
          {comment.author}
        </Typography>
      </Box>

      {/* Comment Text */}
      <Box
        sx={{
          fontSize: "13px",
          lineHeight: 1.2,
          marginBottom: 1,
          whiteSpace: "break-spaces",
          "p": {
            marginBottom: 1,
            marginTop: 1,
          },
          "p:first-of-type": {
            marginTop: 0,
          },
          "p:last-of-type": {
            marginBottom: 0,
          },
        }}
      >
        {renderLeadTextWithMentions(comment.text, comment.mentions)}
      </Box>

      {/* Footer */}
      <Grid container alignItems="center" justifyContent="flex-end">
        <Grid item xs={1}>
          <Tooltip title={moment(comment.commentDate).format("LLLL")} arrow>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: "12px" }}
            >
              {getTimeAgo(comment.commentDate)}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid item xs={1}>
          <ReactionButton postId={comment.commentId} onLikePost={onLikeComment} />
        </Grid>
        <Grid
          item
          xs={10}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <EmoticonDisplay likes={comment.likes} />
        </Grid>
      </Grid>
    </Box>
  );
};

const Comments = ({ comments, setNews }) => {
  const [showAllComments, setShowAllComments] = useState(false);
  const [likeComment] = useMutation(UPDATE_COMMENT_LIKE);

  const handleShowAllComments = () => {
    setShowAllComments(true);
  };

  const handleLikeComment = async (commentId, emoticon) => {
    try {

      const { data } = await likeComment({
        variables: {
          commentId: commentId,
          emoticon,
        },
      });

      const updatedReaction = data.newsContext.updateCommentLike;

      setNews((prevNews) =>
        prevNews.map((newsItem) => ({
          ...newsItem,
          comments: newsItem.comments.map((c) => {
            if (c.commentId === commentId) {
              const updatedLikes =
                emoticon === 0
                  ? c.likes.filter((like) => like.userId !== updatedReaction.userId)
                  : [
                    ...c.likes.filter((like) => like.userId !== updatedReaction.userId),
                    updatedReaction,
                  ];
              return { ...c, likes: updatedLikes };
            }
            return c;
          }),
        }))
      );
    } catch (error) {
      console.error("Error updating comment like:", error);
    }
  };

  return (
    comments && comments.length > 0 && (
      <Box sx={{ mt: 2 }}>
        {!showAllComments && comments.length > 1 && (
          <Button onClick={handleShowAllComments} sx={{ textAlign: "left", pl: 0, mb: 1, textTransform: "none" }}>
            Visa fler kommentarer ({comments.length})
          </Button>
        )}

        {showAllComments
          ? comments.map((comment, index) => <Comment key={index} onLikeComment={(postId, emoticon) => handleLikeComment(postId, emoticon)} comment={comment} />)
          : <Comment onLikeComment={(postId, emoticon) => handleLikeComment(postId, emoticon)} comment={comments[comments.length - 1]} />
        }

        <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
      </Box>
    )
  );
};

export default Comments;
